import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  /** Stagger delay in ms per child index */
  staggerMs?: number;
}

/**
 * Returns a ref and visibility state for scroll-triggered reveal animations.
 * Fires once — does not re-hide on scroll out.
 */
export const useScrollReveal = (options?: UseScrollRevealOptions) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || isRevealed) return;

    // Respect reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.disconnect();
        }
      },
      {
        threshold: options?.threshold ?? 0.15,
        rootMargin: options?.rootMargin ?? '0px 0px -40px 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isRevealed, options?.threshold, options?.rootMargin]);

  return { ref, isRevealed };
};

/**
 * Generates inline style for staggered children inside a revealed container.
 */
export const staggerStyle = (index: number, isRevealed: boolean, staggerMs = 80) => ({
  opacity: isRevealed ? 1 : 0,
  transform: isRevealed ? 'translateY(0)' : 'translateY(24px)',
  transition: `opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerMs}ms, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerMs}ms`,
});
